import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { aboutStats, aboutFeatures, aboutMediaReviews, teamMembers, timelineEvents } from '@/mocks/aboutData';

const ldJson = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  "name": "关于韩漫大全",
  "description": "韩漫大全成立于2020年，致力于为中文读者提供最全面的韩国漫画在线阅读服务。",
  "url": "http://szshops.com/about",
  "inLanguage": "zh-CN",
  "mainEntity": {
    "@type": "Organization",
    "name": "韩漫大全",
    "foundingDate": "2020",
    "url": "http://szshops.com/",
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "customer service",
      "email": "hello@szshops.com"
    }
  },
  "breadcrumb": {
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "首页", "item": "http://szshops.com/" },
      { "@type": "ListItem", "position": 2, "name": "关于我们" }
    ]
  }
};

const faqLdJson = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "韩漫大全上的漫画都是免费的吗？",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "是的，本站所有韩国漫画均提供免费在线阅读，无需付费即可畅享全部内容。"
      }
    },
    {
      "@type": "Question",
      "name": "多久更新一次漫画内容？",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "我们与韩国漫画平台保持同步更新，热门连载作品通常每周更新1-2次，具体更新频率请在作品详情页查看。"
      }
    },
    {
      "@type": "Question",
      "name": "如何在手机上阅读漫画？",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "韩漫大全采用响应式设计，自动适配手机、平板和电脑屏幕。您可以直接在手机浏览器中访问本站，获得流畅的阅读体验。"
      }
    },
    {
      "@type": "Question",
      "name": "翻译质量如何保证？",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "我们拥有专业的韩中翻译团队，每位译者都经过严格筛选和培训，确保翻译准确、流畅、符合中文阅读习惯。"
      }
    }
  ]
};

export default function About() {
  const { t } = useTranslation();

  return (
    <>
      <title>关于我们 - 韩漫大全 | 品牌介绍与联系方式</title>
      <meta name="description" content="韩漫大全成立于2020年，致力于为中文读者提供最全面的韩国漫画在线阅读服务。了解我们的品牌故事、核心优势与联系方式。" />
      <link rel="canonical" href="http://szshops.com/about" />
      <script type="application/ld+json">{JSON.stringify(ldJson)}</script>
      <script type="application/ld+json">{JSON.stringify(faqLdJson)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main>
          <div className="pt-20">
            <Breadcrumb items={[{ label: t('breadcrumb.about') }]} />

            <section className="px-6 md:px-10 py-10 md:py-14">
              <div className="max-w-[1280px] mx-auto">
                <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">
                  <div className="lg:w-[40%]">
                    <span className="inline-block px-3 py-1 text-xs font-medium rounded-full border border-foreground-200 text-foreground-500 mb-4">
                      {t('about.header.label')}
                    </span>
                    <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-950 leading-tight mb-2">
                      {t('about.header.title')}
                      <br />
                      <span className="text-secondary-500 font-light">{t('about.header.titleAccent')}</span>
                    </h1>
                    <p className="text-sm text-foreground-500 mt-6 mb-6 leading-relaxed max-w-md">
                      {t('about.header.desc')}
                    </p>
                    <a href="#contact" className="text-sm font-medium text-primary-500 underline underline-offset-4 hover:text-primary-600 transition-colors">
                      {t('about.header.contactLink')}
                    </a>
                  </div>

                  <div className="lg:w-[60%] flex items-end justify-end">
                    <div className="grid grid-cols-2 gap-6 text-right">
                      <div>
                        <p className="text-3xl md:text-4xl font-bold text-foreground-950">{aboutStats.foundedYear}</p>
                        <p className="text-xs text-foreground-500 mt-1">{t('about.header.stats.founded')}</p>
                      </div>
                      <div>
                        <p className="text-3xl md:text-4xl font-bold text-accent-600">{aboutStats.totalUsers}</p>
                        <p className="text-xs text-foreground-500 mt-1">{t('about.header.stats.users')}</p>
                      </div>
                      <div>
                        <p className="text-3xl md:text-4xl font-bold text-secondary-600">{aboutStats.totalWorks}</p>
                        <p className="text-xs text-foreground-500 mt-1">{t('about.header.stats.works')}</p>
                      </div>
                      <div>
                        <p className="text-3xl md:text-4xl font-bold text-primary-600">{aboutStats.dailyActive}</p>
                        <p className="text-xs text-foreground-500 mt-1">{t('about.header.stats.dailyActive')}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section className="bg-background-100 py-16 md:py-20">
              <div className="px-6 md:px-10 max-w-[1280px] mx-auto">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-8">
                  {t('about.features.title')}
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
                  {aboutFeatures.map((feat) => (
                    <div key={feat.id} className="bg-white rounded-xl p-6 md:p-7 border border-background-200/70">
                      <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-primary-50 text-primary-500 mb-4">
                        <i className={`${feat.icon} text-lg`}></i>
                      </span>
                      <h3 className="text-base font-semibold text-foreground-950 mb-2">{feat.title}</h3>
                      <p className="text-xs text-foreground-500 leading-relaxed">{feat.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section className="py-16 md:py-20">
              <div className="px-6 md:px-10 max-w-[1280px] mx-auto">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-8">
                  发展历程
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
                  {timelineEvents.map((event) => (
                    <div key={event.year} className="bg-background-100 rounded-xl p-5 md:p-6">
                      <span className="text-sm font-bold text-primary-500">{event.year}</span>
                      <h3 className="text-base font-semibold text-foreground-950 mt-2 mb-1">{event.title}</h3>
                      <p className="text-xs text-foreground-500 leading-relaxed">{event.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section className="bg-foreground-950 py-14 md:py-18">
              <div className="px-6 md:px-10 max-w-[1280px] mx-auto">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-background-50 mb-8">
                  {t('about.mediaReviews.title')}
                </h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {aboutMediaReviews.map((review) => (
                    <div key={review.id} className="bg-foreground-900/50 rounded-2xl p-8 md:p-10">
                      <i className="ri-double-quotes-l text-4xl text-accent-400/60 mb-4 block"></i>
                      <p className="text-base text-background-50/80 leading-relaxed mb-6">
                        {review.text}
                      </p>
                      <div className="flex items-center gap-3">
                        <img
                          src={review.avatar}
                          alt={review.source}
                          className="w-10 h-10 rounded-lg object-cover"
                          loading="lazy"
                        />
                        <div>
                          <p className="text-sm font-semibold text-background-50">{review.source}</p>
                          <p className="text-xs text-background-50/50">{review.role}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section id="contact" className="py-16 md:py-20 bg-background-50">
              <div className="px-6 md:px-10 max-w-[1280px] mx-auto text-center">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
                  {t('about.cta.line1')} <span className="text-primary-500 italic">{t('about.cta.line2')}</span>
                </h2>
                <p className="text-base text-foreground-500 mb-8">{t('about.cta.line3')}</p>

                <div className="max-w-lg mx-auto bg-background-100 rounded-xl p-6 md:p-8 mb-8">
                  <div className="grid grid-cols-2 gap-4 text-left">
                    <div>
                      <p className="text-xs text-foreground-500 mb-1">联系邮箱</p>
                      <p className="text-sm font-medium text-foreground-950">hello@szshops.com</p>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500 mb-1">办公地址</p>
                      <p className="text-sm font-medium text-foreground-950">韩国首尔特别市江南区</p>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500 mb-1">客服电话</p>
                      <p className="text-sm font-medium text-foreground-950">+82-2-1234-5678</p>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500 mb-1">工作时间</p>
                      <p className="text-sm font-medium text-foreground-950">周一至周五 9:00-18:00</p>
                    </div>
                  </div>
                </div>

                <a
                  href="mailto:hello@szshops.com"
                  className="inline-block whitespace-nowrap px-8 py-3.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer"
                >
                  {t('about.cta.btn')}
                </a>
              </div>
            </section>

            <section className="py-14 md:py-16 bg-background-100">
              <div className="px-6 md:px-10 max-w-[1280px] mx-auto">
                <h2 className="text-2xl font-heading font-bold text-foreground-950 mb-8 text-center">核心团队</h2>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
                  {teamMembers.map((member) => (
                    <div key={member.name} className="bg-white rounded-xl p-6 text-center border border-background-200/70">
                      <div className="w-14 h-14 mx-auto rounded-full bg-secondary-100 flex items-center justify-center mb-3">
                        <span className="text-lg font-bold text-secondary-600">{member.name[0]}</span>
                      </div>
                      <h3 className="text-sm font-semibold text-foreground-950">{member.name}</h3>
                      <p className="text-xs text-primary-500 mt-0.5">{member.role}</p>
                      <p className="text-[11px] text-foreground-500 mt-1.5">{member.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section id="faq" className="py-16 md:py-20">
              <div className="px-6 md:px-10 max-w-[1280px] mx-auto">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-8">常见问题</h2>
                <div className="max-w-2xl space-y-3">
                  {[
                    { q: '韩漫大全上的漫画都是免费的吗？', a: '是的，本站所有韩国漫画均提供免费在线阅读，无需付费即可畅享全部内容。' },
                    { q: '多久更新一次漫画内容？', a: '我们与韩国漫画平台保持同步更新，热门连载作品通常每周更新1-2次，具体更新频率请在作品详情页查看。' },
                    { q: '如何在手机上阅读漫画？', a: '韩漫大全采用响应式设计，自动适配手机、平板和电脑屏幕。您可以直接在手机浏览器中访问本站，获得流畅的阅读体验。' },
                    { q: '翻译质量如何保证？', a: '我们拥有专业的韩中翻译团队，每位译者都经过严格筛选和培训，确保翻译准确、流畅、符合中文阅读习惯。' },
                  ].map((item, idx) => (
                    <details key={idx} className="bg-background-100 rounded-xl group">
                      <summary className="px-6 py-4 text-sm font-semibold text-foreground-950 cursor-pointer list-none flex items-center justify-between">
                        {item.q}
                        <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                      </summary>
                      <p className="px-6 pb-4 text-sm text-foreground-600 leading-relaxed">{item.a}</p>
                    </details>
                  ))}
                </div>
              </div>
            </section>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}